import React from "react";
import { Box, Checkbox } from "@chakra-ui/react";
import { ColumnMeta, createColumnHelper, Row, Table, VisibilityState } from "@tanstack/react-table";
import { TDealer } from "api/dealershipApi";
import { TMoneyListResults } from "api/useGetReports";
import { EOrderingReports, TAdvancedReportResultsV2 } from "api/useGetStatistics";
import Badge from "components/Badge";
import LeadInfo from "components/v2/Common/LeadInfo";
import { TInventoryOptionValue } from "constants/common";
import { EAPIProviderRanks, EAPIProviderRanksReverse, EProviderInitials } from "types/APIProviderRanks";
// import {
//   EProviderInitials,
//   IPairProviderRatings,
// } from "types/APIProviderRanks";
import TAggregatorColumnPDF from "types/TAggregatorColumnPDF";
import TAggregatorColumnXLS from "types/TAggregatorColumnXLS";
import getProviderRank from "utils/getProviderRank";
// import getDealRatingPair from "utils/getDealRatingPair";
// import getProviderRatingCell from "utils/getProviderRatingCell";
// import getProviderRatingCellPDF from "utils/getProviderRatingCellPDF";

const columnHelper = createColumnHelper<TMoneyListResults>();

const defaultMeta: ColumnMeta<TMoneyListResults, string> = {
  headerStyle: {
    textAlign: 'center',
    fontWeight: 'bold',
  },
  cellStyle: {
    textAlign: 'center',
  },
};

  export const getColumns = (inventory: TInventoryOptionValue, dealer: TDealer) => ([
    columnHelper.accessor('prioritize' as any, {
      id: 'prioritize',
      header: ({ table }) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <input
            type="checkbox"
            checked={table.getIsAllPageRowsSelected()}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
          />
        </Box>
      ),
      cell: ({ row }: { row: Row<TMoneyListResults> }) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
            <input
              type="checkbox"
              checked={row.getIsSelected()}
              onChange={row.getToggleSelectedHandler()}
              onClick={(e) => e.stopPropagation()}
            />
        </Box>
      ),
      size: 50,
      enableResizing: false,
      enableSorting: false,
      meta: {
        ...defaultMeta,
        headerStyle: {
          textAlign: 'center',
        },
        cellStyle: {
          textAlign: 'center',
        },
        showTooltip: false,
        showHeaderTooltip: false,
      },
    }),
    columnHelper.accessor("stock_number", {
      cell: (info) => info.getValue() ?? '-',
      header: 'Stock #',
      id: "stock_number",
      meta: {
        ...defaultMeta
      }
    }),
    columnHelper.accessor("vin", {
      cell: (info) =>
        info.getValue() === null
          ? "N/A"
          : info.getValue(),
      header: 'VIN',
      id: "vin",
      meta: {
        ...defaultMeta
      }
      // default to unchecked
    }),
    columnHelper.accessor('vehicle',{
        cell: (info) => info.getValue(),
        header: 'Vehicle',
        id: "vehicle",
        size: 350,
        meta: {
          ...defaultMeta
        }
      }
    ),
    ...(inventory === 'used' ? [
      columnHelper.accessor("is_certified", {
          id: "is_certified",
          cell: (info) => info.getValue() ? 'Yes' : 'No',
          header: 'Certified',
      })
    ] : []),
    // columnHelper.accessor("marketing_vdps_needed", {
    //   cell: (info) => info.getValue() || 0,
    //   header: 'Marketing VDPS Needed',
    //   id: "marketing_vdps_needed",
    //   size: 300,
    // }),
    ...(inventory === 'used' ? [
      {
        filterColumnId: EProviderInitials.CarGurus,
        ...columnHelper.accessor('cgr_deal_rating', {
            cell: (info) => {
              const { color, icon, label } = getProviderRank(info.getValue() as EAPIProviderRanks);
              return (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Badge color={color} icon={icon} label={label} size="sm" />
                </Box>
              );
            },
            header: dealer.is_lite ? 'Deal Rating' : `CarGurus Rank`,
            id: "cgr_deal_rating",
            size: 250,
            meta: {
              ...defaultMeta
            }
          }
        ),
      },
    ] : []),
    columnHelper.accessor('vdp_count', {
      id: "vdp_count",
      cell: (info) => info.getValue(),
      header: 'VDPs',
      size: 150,
      meta: {
        ...defaultMeta
      }
    }),
    columnHelper.accessor("lead_count", {
      cell: (info) => `${info.getValue()}`,
      header: 'Leads',
      id: "lead_count",
      size: 150,
      meta: {
        ...defaultMeta
      }
    }),
    columnHelper.accessor("price", {
      cell: (info) => `${info.getValue()}`,
      header: 'Price',
      id: "price",
      size: 150,
      meta: {
        ...defaultMeta
      }
    }),
    ...(inventory === 'used' && dealer.is_vauto_used ? [
      columnHelper.accessor("default_percentage_to_market", {
        cell: (info) =>
          info.getValue() === null
            ? "N/A"
            : `${(info.getValue() * 100).toLocaleString()}%`,
        header: 'Default % of Market',
        id: "default_percentage_to_market",
        size: 250,
        meta: {
          ...defaultMeta
        }
      })
    ] : []),
    // ...(inventory === 'new' ? [
    //   columnHelper.accessor("price_to_market", {
    //     cell: (info) =>
    //       info.getValue() === null
    //         ? "N/A"
    //         : `${(info.getValue() * 100).toLocaleString()}%`,
    //     header: 'Price to Market',
    //     id: "percentage_to_market",
    //     size: 250,
    //   }),
    // ] : []),
    columnHelper.accessor("dol", {
      cell: (info) => info.getValue(),
      header: 'DOL',
      id: "dol",
      size: 150,
      meta: {
        ...defaultMeta
      }
    }),
    columnHelper.accessor('vdps_dol', {
      id: "vdps_dol",
      cell: (info) => info.getValue(),
      header: 'VDPS/DOL',
      size: 150,
      meta: {
        ...defaultMeta
      }
    }),
    columnHelper.accessor("photos_count", {
      cell: (info) => info.getValue().toLocaleString(),
      header: 'Photos',
      id: "photos_count",
      size: 150,
      meta: {
        ...defaultMeta
      }
    }),
    // ...(inventory === 'used' && dealer.is_carfax ? [
    //   columnHelper.accessor("carfax_pull_count", {
    //     cell: (info) => info.getValue() || 0,
    //     header: 'Carfax Pulls',
    //     id: "carfax_pull_count",
    //     size: 200,
    //   }),
    // ] : []),
  ]);

export const getPdfColumns = (inventory: TInventoryOptionValue, dealer: TDealer, columnVisibility: VisibilityState): TAggregatorColumnPDF<TMoneyListResults>[] => {
  const columns: TAggregatorColumnPDF<TMoneyListResults>[] = [
    {
      label: "Stock #",
      key: "stock_number",
      func(value): any {
        return value ?? "-";
      }
    },
    {
      label: "VIN",
      key: "vin",
      func(value): any {
        return value ?? "-";
      }
    },
    {
      label: "Vehicle",
      key: "vehicle",
      func(value): any {
        return value ?? "-";
      }
    },
    ... (inventory === 'used' ? [
      {
        label: "Certified",
        key: "is_certified",
        func(value): any {
          return value ? 'Yes' : 'No';
        }
      },
    ] : []) as TAggregatorColumnPDF<TMoneyListResults>[],
    // {
    //   label: "Market VDPS Needed",
    //   key: "marketing_vdps_needed",
    //   func(value): any {
    //     return value ?? "-";
    //   }
    // },
     ...(inventory === 'used' ? [
      {
        label: dealer.is_lite ? 'Deal Rating' : `CarGurus Rank`,
        key: "cgr_deal_rating",
        func(value) {
          return EAPIProviderRanksReverse[value as EAPIProviderRanks] || "-";
        }
      },
    ] : []) as TAggregatorColumnPDF<TMoneyListResults>[],
    {
      label: "VDPs",
      key: "vdp_count",
      func(value): any {
        return value ?? "-";
      }
    },
    {
      label: "Leads",
      key: "lead_count",
      func(value): any {
        return value ?? "-";
      }
    },
    {
      label: "Price",
      key: "price",
      func(value): any {
        return value ?? "-";
      }
    },
    ...(inventory === 'used' && dealer.is_vauto_used ? [
      {
        label: "Default % of Market",
        key: "default_percentage_to_market",
        func: (value) =>
          value === null
            ? "N/A"
            : `${(((value as number) || 0) * 100).toLocaleString()}%`,
      },
    ] : []) as TAggregatorColumnPDF<TMoneyListResults>[],
    // ...(inventory === 'new' ? [
    //   {
    //     label: "Price to Market",
    //     key: "price_to_market",
    //     func: (value) =>
    //       value === null
    //         ? "N/A"
    //         : `${(((value as number) || 0) * 100).toLocaleString()}%`,
    //   },
    // ] : []) as TAggregatorColumnPDF<TMoneyListResults>[],
    {
      label: "DOL",
      key: "dol",
      func(value): any {
        return value ?? "-";
      }
    },
    {
      label: "VDPS/DOL",
      key: "vdps_dol",
      func(value): any {
        return value ?? "-";
      }
    },
    {
      label: "Photos",
      key: "photos_count",
      func(value): any {
        return value ?? "-";
      }
    },
    // ...(inventory === 'used' && dealer.is_carfax ? [
    //   {
    //     label: "Carfax Pulls",
    //     key: "carfax_pull_count",
    //     // func: (value) => value?.toLocaleString() || 0,
    //   },
    // ] : []) as TAggregatorColumnPDF<TMoneyListResults>[],
  ];

  return columns.filter(column => columnVisibility[column.key] !== false);
};

export const getXLSColumns = (inventory: TInventoryOptionValue, dealer: TDealer, columnVisibility: VisibilityState): TAggregatorColumnXLS<TMoneyListResults>[] => {
  const columns: TAggregatorColumnXLS<TMoneyListResults>[] = [
    {
      label: "Stock #",
      key: "stock_number",
      func(value): any {
        return value ?? "-";
      }
    },
      {
      label: "VIN",
      key: "vin",
      func(value): any {
        return value ?? "-";
      }
    },
    {
      label: "Vehicle",
      key: "vehicle",
      func(value): any {
        return value ?? "-";
      }
    },
    ...(inventory === 'used' ? [
      {
        label: "Certified",
        key: "is_certified",
        func(value): any {
          return value ? 'Yes' : 'No';
        }
      },
    ] : []) as TAggregatorColumnXLS<TMoneyListResults>[],
    // {
    //   label: "Market VDPS Needed",
    //   key: "marketing_vdps_needed",
    //   func(value): any {
    //     return value ?? "-";
    //   }
    // },
    ...(inventory === 'used' ? [
      {
        label: dealer.is_lite ? 'Deal Rating' : `CarGurus Rank`,
        key: "cgr_deal_rating",
        func(value) {
          return EAPIProviderRanksReverse[value as EAPIProviderRanks] || "-";
        }
      },
    ] : []) as TAggregatorColumnXLS<TMoneyListResults>[],
    {
      label: "VDPs",
      key: "vdp_count",
      func(value): any {
        return value ?? "-";
      }
    },
    {
      label: "Leads",
      key: "lead_count",
      func(value): any {
        return value ?? "-";
      }
    },
    {
      label: "Price",
      key: "price",
      func(value): any {
        return value ?? "-";
      }
    },
    ...(inventory === 'used' && dealer.is_vauto_used ? [
      {
        label: "Default % of Market",
        key: "default_percentage_to_market",
        func: (value) =>
          value === null
            ? "N/A"
            : `${(((value as number) || 0) * 100).toLocaleString()}%`,
      },
    ] : []) as TAggregatorColumnPDF<TMoneyListResults>[],
    // ...(inventory === 'new' ? [
    //   {
    //     label: "Price to Market",
    //     key: "price_to_market",
    //     func: (value) =>
    //       value === null
    //         ? "N/A"
    //         : `${(((value as number) || 0) * 100).toLocaleString()}%`,
    //   },
    // ] : []) as TAggregatorColumnPDF<TMoneyListResults>[],
    {
      label: "DOL",
      key: "dol",
      func(value): any {
        return value ?? "-";
      }
    },
    {
      label: "VDPS/DOL",
      key: "vdps_dol",
      func(value): any {
        return value ?? "-";
      }
    },
    {
      label: "Photos",
      key: "photos_count",
      func(value): any {
        return value ?? "-";
      }
    },
    // ...(inventory === 'used' && dealer.is_carfax ? [
    //   {
    //     label: "Carfax Pulls",
    //     key: "carfax_pull_count",
    //     func(value): any {
    //       return value ?? "-";
    //     }
    //     // func: (value) => value?.toLocaleString() || 0,
    //   },
    // ] : []) as TAggregatorColumnPDF<TMoneyListResults>[],
  ];

  return columns.filter(column => columnVisibility[column.key] !== false);

};

export enum EMoneyListTabs {
    Conservative = "conservative",
    Aggressive = "aggressive",
    Prioritize = "prioritize",
}


export const tableTabs = [
  { id: EMoneyListTabs.Conservative, label: "Conservative" },
  { id: EMoneyListTabs.Aggressive, label: "Aggressive" },
  { id: EMoneyListTabs.Prioritize, label: "Custom" }
  // { id: EOrderingReports.VDPBoost, label: "VDP Boost" },
];

export const tablePDFFileNames = {
  [EMoneyListTabs.Conservative]: "conservative",
  [EMoneyListTabs.Aggressive]: "aggressive",
  [EMoneyListTabs.Prioritize]: "prioritize",
};